import {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import {Box, Typography} from '@mui/material';

import {ProfileComparisonProfileGrid} from './ProfileComparisonProfileGrid';
import {ProfileComparisonResult} from './ProfileComparisonResult';
import {profileComparisonSectionHeadingSx, profileComparisonSectionSubtitleSx} from './profileComparisonSectionStyles';
import {ProfileComparisonSelectionTray} from './ProfileComparisonSelectionTray';
import {useProfileComparisonFilters} from '../../hooks/useProfileComparisonFilters';
import {useProfileComparisonProfiles} from '../../hooks/useProfileComparisonProfiles';
import {useProfileComparisonSelection} from '../../hooks/useProfileComparisonSelection';
import {filterProfileComparisonProfiles} from '../../utils/ProfileComparison/filterProfileComparisonProfiles';

export interface Props {
  companyId: string;
  departmentId: string;
  departmentName: string;
}

export const ProfileComparisonSectionContent = ({companyId, departmentId, departmentName}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDepartmentHealthPage.profileComparison'});
  const {profiles, isLoading, error} = useProfileComparisonProfiles(companyId, departmentId);
  const {filters, setFilters, seniorityOptions, hasActiveFilters, clearFilters} = useProfileComparisonFilters(profiles);
  const {profileAId, profileBId, isComparisonReady, toggleProfile, deselectSlot} = useProfileComparisonSelection();

  const filteredProfiles = useMemo(() => filterProfileComparisonProfiles(profiles, filters), [filters, profiles]);

  const profileById = useMemo(() => new Map(profiles.map((profile) => [profile.resourceId, profile])), [profiles]);

  const profileA = profileAId ? profileById.get(profileAId) ?? null : null;
  const profileB = profileBId ? profileById.get(profileBId) ?? null : null;

  if (error) {
    return (
      <Box sx={{mt: 2}}>
        <Typography variant='body2' color='error'>
          {t('loadingError')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box data-testid='profile-comparison-section'>
      <Box sx={{mt: 2.5, mb: 2}}>
        <Box
          component='h2'
          sx={{
            ...profileComparisonSectionHeadingSx,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            m: 0,
          }}
        >
          <CompareArrowsOutlinedIcon sx={{fontSize: 22, color: 'text.secondary'}} />
          {t('title')}
        </Box>
        <Typography sx={profileComparisonSectionSubtitleSx}>{t('subtitle')}</Typography>
      </Box>
      <ProfileComparisonProfileGrid
        profiles={filteredProfiles}
        profileAId={profileAId}
        profileBId={profileBId}
        isLoading={isLoading}
        filters={filters}
        seniorityOptions={seniorityOptions}
        departmentName={departmentName}
        hasActiveFilters={hasActiveFilters}
        onFiltersChange={setFilters}
        onClearFilters={clearFilters}
        onProfileClick={toggleProfile}
      />
      <ProfileComparisonSelectionTray
        profileA={profileA}
        profileB={profileB}
        onDeselectA={() => deselectSlot('a')}
        onDeselectB={() => deselectSlot('b')}
      />
      {isComparisonReady && profileAId && profileBId && (
        <ProfileComparisonResult
          companyId={companyId}
          profileAPublicId={profileA?.publicId ?? null}
          profileBPublicId={profileB?.publicId ?? null}
        />
      )}
    </Box>
  );
};
